import { Box, type BoxProps, styled, useTheme } from "@mui/material"
import { forwardRef } from "react"
import {
  CheckCircleThinBorderIcon,
  MinusCircleThinBorderIcon,
} from "../../icons/IconUrls"

export type AchievementBadgeProps = BoxProps & {
  /** Whether the goal is achieved (e.g. a value within its norm). Drives the color and the icon. */
  isAchieved?: boolean
  size?: number
}

export const useAchievementColor = (isAchieved: boolean) => {
  const { palette } = useTheme()
  const color = isAchieved ? palette.secondary.main : palette.primary.light1
  const bgColor = isAchieved
    ? `color-mix(in srgb, ${color} 12%, white)`
    : palette.common.lightBlue
  return { color, bgColor }
}

export const AchievementBadge = forwardRef<HTMLDivElement, AchievementBadgeProps>(
  ({ isAchieved = false, size = 18, ...props }, ref) => {
    const { bgColor, color } = useAchievementColor(isAchieved)
    const Icon = isAchieved ? CheckCircleThinBorderIcon : MinusCircleThinBorderIcon

    return (
      <IconContainer bgColor={bgColor} ref={ref} {...props}>
        <Icon size={size} color={color} />
      </IconContainer>
    )
  },
)

const IconContainer = styled(Box, {
  shouldForwardProp: (prop) => !(prop === "bgColor"),
})<{ bgColor: string }>(({ bgColor }) => ({
  display: "grid",
  placeContent: "center",
  padding: "3px",
  borderRadius: "1000px",
  backgroundColor: bgColor,
}))
